<template>
  <div v-if="data" class="kpb-map">
    <slot name="top" />
    <LazyLoad @intersect="initMap">
      <div ref="map" class="kpb-map__mapbox" :style="`aspect-ratio: ${aspectRatio}`"></div>
    </LazyLoad>
    <slot name="bottom">
      <div class="kpb-map__address" v-if="data.formatted && displayAddress">
        {{ data.formatted }}
      </div>
    </slot>
  </div>
</template>

<script>
import { Map, Marker, FullscreenControl, NavigationControl } from 'mapbox-gl'
import LazyLoad from 'vue-make-lazy'

/** Map component */
export default {
  props: {
    data: {
      type: Object,
      required: true,
      default: () => ({}),
    },
    variant: String,
    aspectRatio: {
      type: String,
      default: '16/9',
    },
    mapOptions: {
      type: Object,
      default: () => ({}),
    },
    showControls: {
      type: Boolean,
      default: true,
    },
    markerOptions: {
      type: Object,
      default: () => ({
        color: 'var(--primary)',
      }),
    },
    displayAddress: {
      type: Boolean,
      default: true,
    },
  },
  watch: {
    'data.markers': {
      handler: 'initMarkers',
    },
  },
  data() {
    return {
      map: null,
      markers: [],
    }
  },
  computed: {
    coordinates() {
      return this.$path('data.location.coordinates') || this.$path('data.coordinates')
    },
    defaultOptionsComp() {
      return {
        style: process.env.VUE_APP_MAPBOX_STYLE,
        accessToken: process.env.VUE_APP_MAPBOX_ACCESS_TOKEN,
        zoom: 12,
        dragPan: false,
        scrollZoom: false,
        dragRotate: false,
        ...this.mapOptions,
      }
    },
  },
  methods: {
    initMap() {
      if (!this.coordinates) return
      this.map = new Map({
        container: this.$refs.map,
        center: [...this.coordinates],
        ...this.defaultOptionsComp,
      })

      this.initMarkers()
      this.initControls()
    },
    initMarkers() {
      this.markers.forEach(m => m.remove())
      let markers = this.data.markers || [this.coordinates]

      markers.forEach(marker => {
        this.markers.push(
          new Marker({
            ...this.markerOptions,
          })
            .setLngLat([...marker])
            .addTo(this.map),
        )
      })
    },
    initControls() {
      if (!this.showControls) return

      const controls = [new FullscreenControl(), new NavigationControl()]
      controls.forEach(c => this.map.addControl(c))
    },
  },
  components: {
    LazyLoad,
  },
}
</script>

<style lang="scss">
.kpb-map {
  text-align: center;
  background: var(--primary);
  border: none;
  color: var(--primary-contrast);

  &__address {
    font-size: 1.25em;
    padding: 0.5rem;

    @include respond-below('phone') {
      font-size: 1em;
    }
  }
}

.mapboxgl {
  &-ctrl {
    &-attrib,
    &-logo {
      display: none !important;
    }
  }
  &-marker circle {
    fill: var(--primary-contrast);
  }
}
</style>
